import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Label, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';

const ROUTES_PER_PAGE = 20;

const Route = () => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);

  const loadRoutes = async (p) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/route/?p=${p - 1}`);
      const { success, message, data } = res.data;
      if (success) {
        setRoutes(data);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRoutes(page);
  }, [page]);

  const searchRoutes = async () => {
    if (searchKeyword === '') {
      setPage(1);
      loadRoutes(1);
      return;
    }
    setSearching(true);
    try {
      const res = await API.get(`/api/route/search?keyword=${searchKeyword}`);
      const { success, message, data } = res.data;
      if (success) {
        setRoutes(data);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setSearching(false);
  };

  const manageRoute = async (id, action) => {
    if (action === 'delete') {
      await deleteRoute(id);
    }
  };

  const deleteRoute = async (id) => {
    const res = await API.delete(`/api/route/${id}/`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('route.delete_success'));
      await loadRoutes(page);
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('route.title')}</Card.Header>
          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <Input
              icon='search'
              placeholder={t('route.search')}
              value={searchKeyword}
              onChange={(e, { value }) => setSearchKeyword(value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') searchRoutes();
              }}
              disabled={searching}
            />
            <Button
              as={Link}
              to='/route/add'
              primary
            >
              {t('route.add')}
            </Button>
          </div>
          <Table basic compact celled striped style={{ marginTop: 10 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>ID</Table.HeaderCell>
                <Table.HeaderCell>{t('route.name')}</Table.HeaderCell>
                <Table.HeaderCell>{t('route.models')}</Table.HeaderCell>
                <Table.HeaderCell>{t('route.large_models')}</Table.HeaderCell>
                <Table.HeaderCell>{t('route.prompt_token_threshold')}</Table.HeaderCell>
                <Table.HeaderCell>{t('route.status')}</Table.HeaderCell>
                <Table.HeaderCell>{t('route.operations')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {routes.map((route) => (
                <Table.Row key={route.id}>
                  <Table.Cell>{route.id}</Table.Cell>
                  <Table.Cell>{route.name}</Table.Cell>
                  <Table.Cell>{route.models}</Table.Cell>
                  <Table.Cell>{route.large_models || '-'}</Table.Cell>
                  <Table.Cell>{route.prompt_token_threshold || '-'}</Table.Cell>
                  <Table.Cell>
                    <Label color={route.status === 1 ? 'green' : 'grey'}>
                      {route.status === 1 ? t('route.enabled') : t('route.disabled')}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Button size='tiny' as={Link} to={`/route/edit/${route.id}`}>
                      {t('route.edit')}
                    </Button>
                    <Button
                      size='tiny'
                      negative
                      onClick={() => {
                        if (window.confirm(t('route.delete_confirm'))) {
                          manageRoute(route.id, 'delete');
                        }
                      }}
                    >
                      {t('route.delete')}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
              {routes.length === 0 && !loading && (
                <Table.Row>
                  <Table.Cell colSpan='7'>{t('route.none')}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
          <Button
            disabled={page === 1 || searching}
            onClick={() => setPage(page - 1)}
          >
            {t('route.previous')}
          </Button>
          <Button
            disabled={routes.length < ROUTES_PER_PAGE || searching}
            onClick={() => setPage(page + 1)}
          >
            {t('route.next')}
          </Button>
        </Card.Content>
      </Card>
    </div>
  );
};

export default Route;
